import { normalizeMeteringTo01 } from './audioMetering';

/**
 * Peaks stored on the attachment / sent on upload.
 * 64 samples at 4-bit precision → 32 bytes → 44 base64 chars.
 * Playback resamples to display bar count; visually identical to 128 samples.
 */
export const VOICE_MESSAGE_WAVEFORM_STORAGE_SAMPLES = 64;

/**
 * Bars in the **recording** composer strip (dense / thin like playback, fits pill width).
 */
export const VOICE_MESSAGE_WAVEFORM_DISPLAY_BARS = 96;

/**
 * Bars when **playing** a voice message — dense thin bars, ~WhatsApp Web style.
 */
export const VOICE_MESSAGE_WAVEFORM_PLAYBACK_BARS = 100;

/**
 * Linear resample `samples` to exactly `targetCount` values in [0, 1] range preserved.
 */
export function resampleWaveformPeaks(samples, targetCount) {
  if (targetCount < 1) {
    return [];
  }
  if (samples.length === 0) {
    return Array(targetCount).fill(0.08);
  }
  if (samples.length === 1) {
    return Array(targetCount).fill(samples[0]);
  }
  if (samples.length === targetCount) {
    return [...samples];
  }
  const out = [];
  const last = targetCount - 1;
  for (let i = 0; i < targetCount; i += 1) {
    const t = last > 0 ? i / last : 0;
    const idx = t * (samples.length - 1);
    const i0 = Math.floor(idx);
    const i1 = Math.min(i0 + 1, samples.length - 1);
    const f = idx - i0;
    out.push(samples[i0] * (1 - f) + samples[i1] * f);
  }
  return out;
}

const PEAK_STRETCH_MIN_SPAN = 0.04;

/**
 * Per-clip min/max stretch so silence pauses read visibly lower than speech
 * even when the OS maps "quiet" and "loud" into a narrow dB band.
 */
function stretchPeaksToUnitRange(values) {
  if (values.length < 2) {
    return values;
  }
  let lo = values[0];
  let hi = values[0];
  for (let i = 1; i < values.length; i += 1) {
    const v = values[i];
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  const span = hi - lo;
  if (span < PEAK_STRETCH_MIN_SPAN) {
    return values;
  }
  return values.map(v => (v - lo) / span);
}

/**
 * Downsample metering dB samples to storage peaks [0…1].
 * Produces VOICE_MESSAGE_WAVEFORM_STORAGE_SAMPLES values in [0, 1].
 */
export function downsampleMeteringSamplesToPeaks(dbSamples) {
  const n = VOICE_MESSAGE_WAVEFORM_STORAGE_SAMPLES;
  if (dbSamples.length === 0) {
    return Array(n).fill(0.08);
  }
  const normalized = dbSamples.map(normalizeMeteringTo01);
  const scaled = stretchPeaksToUnitRange(normalized);
  if (scaled.length === 1) {
    return Array(n).fill(scaled[0]);
  }
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const t = n > 1 ? i / (n - 1) : 0;
    const idx = t * (scaled.length - 1);
    const i0 = Math.floor(idx);
    const i1 = Math.min(i0 + 1, scaled.length - 1);
    const f = idx - i0;
    const v = scaled[i0] * (1 - f) + scaled[i1] * f;
    out.push(Math.round(v * 1000) / 1000);
  }
  return out;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_REV = new Uint8Array(128);
for (let i = 0; i < B64.length; i += 1) B64_REV[B64.charCodeAt(i)] = i;

/**
 * Nibble-pack float peaks [0…1] → base64 string.
 * Two peaks per byte (4 bits each, 16 levels), then standard base64 with padding.
 * 64 peaks → 32 bytes → 44 chars.
 */
export function encodePeaksToBase64(peaks) {
  const byteLen = Math.ceil(peaks.length / 2);
  const bytes = new Uint8Array(byteLen);
  for (let i = 0; i < peaks.length; i += 1) {
    const nibble = Math.round(Math.max(0, Math.min(1, peaks[i])) * 15);
    if (i % 2 === 0) {
      bytes[i >> 1] = nibble << 4;
    } else {
      bytes[i >> 1] |= nibble;
    }
  }
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] : 0;
    out += B64[b0 >> 2];
    out += B64[((b0 & 0x03) << 4) | (b1 >> 4)];
    out += i + 1 < bytes.length ? B64[((b1 & 0x0f) << 2) | (b2 >> 6)] : '=';
    out += i + 2 < bytes.length ? B64[b2 & 0x3f] : '=';
  }
  return out;
}

/** Decode nibble-packed base64 → float peaks [0…1]. */
export function decodePeaksFromBase64(str) {
  const clean = str.replace(/=+$/, '');
  const remainder = clean.length % 4;
  const fullGroups = Math.floor(clean.length / 4);
  const byteLen = fullGroups * 3 + (remainder > 0 ? remainder - 1 : 0);
  if (byteLen <= 0) return [];
  const bytes = new Uint8Array(byteLen);
  let j = 0;
  for (let i = 0; i < clean.length; i += 4) {
    const c0 = B64_REV[clean.charCodeAt(i)] || 0;
    const c1 = i + 1 < clean.length ? B64_REV[clean.charCodeAt(i + 1)] || 0 : 0;
    const c2 = i + 2 < clean.length ? B64_REV[clean.charCodeAt(i + 2)] || 0 : 0;
    const c3 = i + 3 < clean.length ? B64_REV[clean.charCodeAt(i + 3)] || 0 : 0;
    bytes[j++] = (c0 << 2) | (c1 >> 4);
    if (j < byteLen) bytes[j++] = ((c1 & 0x0f) << 4) | (c2 >> 2);
    if (j < byteLen) bytes[j++] = ((c2 & 0x03) << 6) | c3;
  }
  const peaks = [];
  for (let i = 0; i < bytes.length; i += 1) {
    peaks.push((bytes[i] >> 4) / 15);
    peaks.push((bytes[i] & 0x0f) / 15);
  }
  return peaks;
}

/**
 * Parse waveform peaks from API attachment `meta.waveformData` (nibble-packed base64).
 * Returns null when missing or empty (voice messages recorded before waveform support).
 * Resamples to VOICE_MESSAGE_WAVEFORM_STORAGE_SAMPLES.
 */
export function peaksFromAttachmentMeta(meta) {
  if (!meta || typeof meta !== 'object') {
    return null;
  }
  if (typeof meta.waveformData !== 'string' || meta.waveformData.length === 0) {
    return null;
  }
  const decoded = decodePeaksFromBase64(meta.waveformData);
  if (decoded.length === 0) {
    return null;
  }
  return resampleWaveformPeaks(decoded, VOICE_MESSAGE_WAVEFORM_STORAGE_SAMPLES);
}

/**
 * Stored peaks → heights for **playback** strip (many thin bars).
 */
export function waveformHeightsForDisplay(storedPeaks) {
  return resampleWaveformPeaks(storedPeaks, VOICE_MESSAGE_WAVEFORM_PLAYBACK_BARS);
}
